import { chatDrawerActions } from 'ducks/chatDrawer'
import { isActionWhitelisted, requiresConfirmation } from 'layout/widgets/adaChat/actionPermissions'
import { osFetch } from 'util/fetch'
import {
  InverterDesignAssistantArgs,
  runInverterDesignAssistantCore,
} from 'layout/widgets/adaChat/utils/runInverterDesignAssistantCore'
import {
  isInDesignTab,
  validateActionRequirementsOrThrow,
} from 'layout/widgets/adaChat/utils/validateActionRequirements'
import { LiveFunctionCall } from '../GeminiLiveAPI/lib/multimodal-live-types'

interface RunInverterDesignAssistantArgs extends InverterDesignAssistantArgs {
  confirmed?: boolean
}

export const TOOL_NAME = 'run_inverter_design_assistant'

export async function RunInverterDesignAssistantToolCall(
  functionCall: LiveFunctionCall,
  history: any,
  orgId: number | undefined
) {
  const args = functionCall.args as RunInverterDesignAssistantArgs

  console.log('[Voice] RunInverterDesignAssistantToolCall', args)

  if (!orgId) {
    throw new Error('Organization ID is required to run inverter design assistant')
  }

  const pathname = window.location.pathname
  validateActionRequirementsOrThrow('runInverterDesignAssistant', pathname)

  // Extract project ID from pathname
  const { projectId } = isInDesignTab(pathname)

  if (!projectId) {
    throw new Error('Project ID is required to run inverter design assistant')
  }

  // Check if this action requires confirmation
  const isWhitelisted = isActionWhitelisted('runInverterDesignAssistant')

  if (requiresConfirmation('runInverterDesignAssistant') && !isWhitelisted) {
    if (!args.confirmed) {
      const errorMessage =
        'This action requires confirmation. Please ask the user: "Would you like me to get inverter recommendations for this system?" and wait for their confirmation before proceeding.'
      console.error('[Voice] Inverter design assistant requires confirmation but confirmed flag not set:', errorMessage)
      throw new Error(errorMessage)
    }
  }

  // Get the selected system
  const system = window.editor?.selectedSystem
  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  console.log('[Voice] Starting runInverterDesignAssistant for system:', system.uuid)

  // Create a message with toolExecutionState
  if (window.reduxStore && projectId) {
    const state = window.reduxStore.getState()
    const messages = state?.chatDrawer?.messages || []
    const lastMessageIndex = messages.length > 0 ? messages.length - 1 : -1

    if (lastMessageIndex >= 0) {
      window.reduxStore.dispatch(
        chatDrawerActions.updateToolExecutionState(lastMessageIndex, {
          toolType: 'inverterDesignAssistant',
          status: 'running',
          projectId: projectId,
          isAutoAccepted: isWhitelisted,
        })
      )
    } else {
      window.reduxStore.dispatch(
        chatDrawerActions.addMessage('Getting inverter recommendations...', 'receive', 'voice')
      )
      const newState = window.reduxStore.getState()
      const newMessages = newState?.chatDrawer?.messages || []
      const newLastMessageIndex = newMessages.length > 0 ? newMessages.length - 1 : -1
      if (newLastMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(newLastMessageIndex, {
            toolType: 'inverterDesignAssistant',
            status: 'running',
            projectId: projectId,
            isAutoAccepted: isWhitelisted,
          })
        )
      }
    }
  }

  // Call the API to get inverter recommendations
  if (!window.reduxStore) {
    throw new Error('Redux store not available')
  }

  try {
    // Get country ISO2 from Redux store
    const state = window.reduxStore?.getState()
    const countryIso2 = state ? (state as any).orgs?.org?.country_iso2 || '' : ''
    const orderByCodes: string[] = [] // Default empty

    // Use shared core logic
    const result = await runInverterDesignAssistantCore(args, system.uuid, async (params) => {
      // Override the placeholder values with actual values
      params.orgId = orgId
      params.countryIso2 = countryIso2
      params.orderByCodes = orderByCodes

      // Build query params (everything except auto_string goes in query)
      const queryParams = new URLSearchParams()
      queryParams.append('page', '1')
      queryParams.append('limit', String(params.limit))
      queryParams.append('ordering', '_default_rule')
      queryParams.append('fieldset', 'list')
      queryParams.append('types', JSON.stringify(['inverter']))
      queryParams.append('show_archived', 'false')
      if (params.inverterType) {
        queryParams.append('inverter_type', params.inverterType)
      }
      if (params.countryIso2) {
        queryParams.append('country_iso2', params.countryIso2)
      }
      queryParams.append('order_by_codes', JSON.stringify(params.orderByCodes))

      const response = await osFetch(`${window.API_ROOT}/api/orgs/${orgId}/componentsv2/?${queryParams.toString()}`, {
        method: 'POST',
        headers: window.Utils.tokenAuthHeaders({
          'X-CSRFToken': window.getCookie('csrftoken'),
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          auto_string: params.autoStringParams,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      const responseData = await response.json()

      // Handle response format
      return responseData.results || responseData.data || responseData || []
    })

    // Update the message to show ready state
    if (window.reduxStore) {
      const state = window.reduxStore.getState()
      const messages = state?.chatDrawer?.messages || []

      let targetMessageIndex = -1
      for (let i = messages.length - 1; i >= 0; i--) {
        if (
          messages[i].direction === 'receive' &&
          messages[i].toolExecutionState &&
          messages[i].toolExecutionState.toolType === 'inverterDesignAssistant'
        ) {
          targetMessageIndex = i
          break
        }
      }

      if (targetMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
            toolType: 'inverterDesignAssistant',
            status: 'ready',
            description: result.description,
            data: { inverterSets: result.inverterSets },
            projectId: projectId,
          })
        )
        console.log('[Voice] Inverter design assistant completed, recommendations:', result.inverterSets.length)
      }
    }

    // Format recommendations for voice reading
    const formatInverterRecommendations = (inverterSets: any[]): string => {
      if (!inverterSets || inverterSets.length === 0) {
        return result.description
      }

      const recommendations = inverterSets.slice(0, 3).map((inverterSet: any, index: number) => {
        const firstComponent = inverterSet.components?.[0]
        const inverterName = firstComponent?.name || firstComponent?.code || `Option ${index + 1}`
        const manufacturer = firstComponent?.manufacturer_name || firstComponent?.manufacturer
        const combinedRating = inverterSet.combined_rating
        const quantity = inverterSet.components?.reduce((sum: number, c: any) => sum + (c.quantity || 1), 0) || 0
        const efficiency = firstComponent?.efficiency || firstComponent?.data?.efficiency

        let description = `Option ${index + 1}: ${manufacturer ? `${manufacturer} ` : ''}${inverterName}`
        if (combinedRating) {
          description += `, ${combinedRating} kilowatts combined rating`
        }
        if (quantity > 0) {
          description += `, quantity of ${quantity}`
        }
        if (efficiency) {
          description += `, ${efficiency} percent efficiency`
        }

        return description
      })

      return `Found ${inverterSets.length} inverter recommendation${inverterSets.length > 1 ? 's' : ''}. ${recommendations.join('. ')}. To load a recommendation, say "load option 1" for the first option, "load option 2" for the second option, or "load option 3" for the third option.`
    }

    return {
      success: true,
      message: formatInverterRecommendations(result.inverterSets),
    }
  } catch (error: any) {
    console.error('[Voice] Inverter design assistant error:', error)

    // Update the toolExecutionState to 'error'
    if (window.reduxStore && projectId) {
      const state = window.reduxStore.getState()
      const messages = state?.chatDrawer?.messages || []

      let targetMessageIndex = -1
      for (let i = messages.length - 1; i >= 0; i--) {
        if (
          messages[i].direction === 'receive' &&
          messages[i].toolExecutionState &&
          messages[i].toolExecutionState.toolType === 'inverterDesignAssistant'
        ) {
          targetMessageIndex = i
          break
        }
      }

      if (targetMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
            toolType: 'inverterDesignAssistant',
            status: 'error',
            projectId: projectId,
            description: error?.message || 'Inverter design assistant failed',
            error: {
              message: error?.message || 'Inverter design assistant failed',
            },
          })
        )
      }
    }

    throw error
  }
}
